import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type {
	ConnectorPromoConfig,
	RecommendedConnector,
} from '../connector-promo-config-types.ts';
import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';

import { ConnectorIconPlaceholder } from './ConnectorIconPlaceholder.tsx';

const messages = defineMessages({
	heading: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.emptyState.heading',
		defaultMessage: 'Seek and you shall find',
		description: 'Heading for the empty state connector promo',
	},
	body: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.emptyState.body',
		defaultMessage:
			'Search or ask Rovo to find what you need across Atlassian, and connect your other apps to unlock search across them, too.',
		description: 'Description text for the empty state connector promo',
	},
	connectMoreApps: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.emptyState.connectMoreApps',
		defaultMessage: 'Connect Rovo to more apps',
		description: 'Link text to navigate to the admin page for connecting more apps',
	},
});

type ConnectorEmptyStatePromoProps = Pick<
	ConnectorPromoConfig,
	'recommendedConnectors' | 'onSetupConnector'
> & {
	testId?: string;
};

const styles = cssMap({
	container: {
		marginTop: token('space.200'),
	},
	body: {
		maxWidth: '480px',
		textAlign: 'center',
	},
	connectorButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.025'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.input'),
		cursor: 'pointer',
		color: token('color.text'),
	},
	connectMoreLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.text.subtle'),
	},
});

const ConnectorPressableItem = ({
	connector,
	onSetup,
	testId,
}: {
	connector: RecommendedConnector;
	onSetup: (key: string) => void;
	testId: string;
}) => (
	<Pressable xcss={styles.connectorButton} onClick={() => onSetup(connector.key)} testId={testId}>
		<ConnectorIconPlaceholder connector={connector} size={20} />
		{connector.displayName}
	</Pressable>
);

export const ConnectorEmptyStatePromo = ({
	recommendedConnectors,
	onSetupConnector,
	testId = 'connector-empty-state-promo',
}: ConnectorEmptyStatePromoProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const adminHubLink = useAdminHubConnectorLink();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const handleSetupClick = useCallback(
		(key: string) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'ConnectorEmptyStatePromo',
					action: 'clicked',
					actionSubject: 'button',
					attributes: {
						connectorKey: key,
						container: 'ConnectorPromoNavSearch',
						testId: `${testId}--connector-${key}`,
					},
				},
			}).fire();
			onSetupConnector(key);
		},
		[onSetupConnector, createEvent, testId],
	);

	return (
		<Box xcss={styles.container} testId={testId}>
			<Stack space="space.200" alignInline="center">
				<Heading size="medium">{formatMessage(messages.heading)}</Heading>
				<Box xcss={styles.body}>
					<Text color="color.text.subtle" align="center">
						{formatMessage(messages.body)}
					</Text>
				</Box>
				<Inline space="space.100" shouldWrap alignInline="center">
					{recommendedConnectors.slice(0, 3).map((connector) => (
						<ConnectorPressableItem
							key={connector.key}
							connector={connector}
							onSetup={handleSetupClick}
							testId={`${testId}--connector-${connector.key}`}
						/>
					))}
				</Inline>
				<Anchor
					href={adminHubLink ?? '/admin'}
					xcss={styles.connectMoreLink}
					testId={`${testId}--connect-more`}
				>
					{formatMessage(messages.connectMoreApps)}
					<LinkExternalIcon label="" color="currentColor" />
				</Anchor>
			</Stack>
		</Box>
	);
};
